import React, { useEffect, useState, useCallback } from "react";
import { Link, NavLink, useNavigate, useLocation } from "react-router-dom";
import { Menu, X, MessageSquare, ShoppingBag } from "lucide-react";
import { NAV, BRAND, CONTACT } from "@/data/site";
import { useCart } from "@/context/CartContext";

function useScrolled(threshold = 24) {
  const [scrolled, setScrolled] = useState(false);
  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > threshold);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, [threshold]);
  return scrolled;
}

function CartButton({ className = "" }) {
  const { count, openCart } = useCart();
  return (
    <button
      type="button"
      data-testid="cart-open-btn"
      onClick={openCart}
      aria-label={`Open cart${count ? `, ${count} items` : ""}`}
      className={`relative inline-flex items-center justify-center text-ivory/85 transition-colors hover:text-gold ${className}`}
    >
      <ShoppingBag size={20} />
      {count > 0 && (
        <span
          data-testid="cart-count-badge"
          className="absolute -right-2 -top-2 flex h-4 min-w-4 items-center justify-center rounded-full bg-gold px-1 text-[0.6rem] font-semibold leading-none text-onyx"
        >
          {count}
        </span>
      )}
    </button>
  );
}

export default function Navbar() {
  const [open, setOpen] = useState(false);
  const scrolled = useScrolled();
  const navigate = useNavigate();
  const location = useLocation();

  const go = useCallback(
    (item, e) => {
      if (item.hash) {
        e.preventDefault();
        setOpen(false);
        if (location.pathname === "/") {
          const el = document.getElementById(item.hash);
          if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
        } else {
          navigate(`/#${item.hash}`);
        }
      } else {
        setOpen(false);
      }
    },
    [location.pathname, navigate]
  );

  useEffect(() => {
    document.body.style.overflow = open ? "hidden" : "";
    return () => (document.body.style.overflow = "");
  }, [open]);

  useEffect(() => {
    const onKey = (e) => e.key === "Escape" && setOpen(false);
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, []);

  const linkBase =
    "relative text-[0.72rem] tracking-[0.25em] uppercase font-light text-ivory/85 hover:text-gold transition-colors duration-300 nav-underline";

  return (
    <>
    <header
      className={`fixed top-0 inset-x-0 z-50 transition-all duration-500 ${
        scrolled
          ? "bg-onyx/95 backdrop-blur-md border-b border-gold/20 py-2.5"
          : "bg-gradient-to-b from-onyx/90 to-transparent py-4"
      }`}
    >
      <nav
        className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-10 flex items-center justify-between"
        aria-label="Primary"
      >
        <Link to="/" className="flex items-center shrink-0" aria-label={`${BRAND.name} home`}>
          <img
            src={BRAND.logo}
            alt={`${BRAND.name} logo`}
            width={260}
            height={166}
            className={`w-auto transition-all duration-500 hover:opacity-90 ${
              scrolled ? "h-14 sm:h-16" : "h-16 sm:h-24"
            }`}
            fetchPriority="high"
          />
        </Link>

        {/* Desktop nav */}
        <div className="hidden lg:flex items-center gap-9">
          {NAV.map((item) =>
            item.hash ? (
              <a key={item.label} href={item.to} onClick={(e) => go(item, e)} className={linkBase}>
                {item.label}
              </a>
            ) : (
              <NavLink
                key={item.label}
                to={item.to}
                end={item.to === "/"}
                className={({ isActive }) => `${linkBase} ${isActive ? "text-gold" : ""}`}
              >
                {item.label}
              </NavLink>
            )
          )}
          <a
            href={CONTACT.smsHref}
            aria-label={`Text us at ${CONTACT.phoneDisplay}`}
            className="inline-flex items-center gap-2 text-[0.72rem] tracking-[0.2em] uppercase text-ivory/85 hover:text-gold transition-colors"
          >
            <MessageSquare size={15} className="text-gold" />
            <span className="hidden xl:inline">Text {CONTACT.phoneDisplay}</span>
          </a>
          <Link
            to="/book"
            className="btn-gold-outline px-6 py-2.5 text-[0.72rem] tracking-[0.25em] uppercase"
          >
            Book Now
          </Link>
          <CartButton />
        </div>

        {/* Mobile toggle */}
        <div className="flex items-center gap-4 lg:hidden">
          <CartButton />
          <button
            type="button"
            onClick={() => setOpen((v) => !v)}
            className="inline-flex items-center justify-center w-11 h-11 text-gold"
            aria-label={open ? "Close menu" : "Open menu"}
            aria-expanded={open}
            aria-controls="mobile-menu"
          >
            {open ? <X size={26} /> : <Menu size={26} />}
          </button>
        </div>
      </nav>
    </header>

      {/* Mobile menu - fully opaque slide-in panel, always readable */}
      <div
        id="mobile-menu"
        className={`lg:hidden fixed inset-0 z-[60] bg-onyx overflow-y-auto transition-transform duration-300 ease-out ${
          open ? "translate-x-0" : "translate-x-full pointer-events-none"
        }`}
        style={{ paddingBottom: "env(safe-area-inset-bottom)" }}
      >
        <div className="flex h-full flex-col px-7 pt-5 pb-8">
          <div className="flex items-center justify-between">
            <img src={BRAND.logo} alt="" width={160} height={100} className="h-11 w-auto" />
            <button
              type="button"
              onClick={() => setOpen(false)}
              aria-label="Close menu"
              className="inline-flex h-11 w-11 items-center justify-center text-gold"
            >
              <X size={28} />
            </button>
          </div>

          <div className="gold-divider my-7" />

          <nav className="flex flex-col gap-7" aria-label="Mobile">
            {NAV.map((item) => (
              <Link
                key={item.label}
                to={item.to}
                onClick={() => setOpen(false)}
                className="font-display text-3xl text-ivory transition-colors hover:text-gold"
              >
                {item.label}
              </Link>
            ))}
          </nav>

          <div className="mt-auto flex flex-col gap-3">
            <Link
              to="/book"
              onClick={() => setOpen(false)}
              className="btn-gold text-center px-6 py-4 text-sm tracking-[0.22em] uppercase"
            >
              Book Now
            </Link>
            <a
              href={CONTACT.smsHref}
              onClick={() => setOpen(false)}
              className="btn-gold-outline text-center px-6 py-4 text-sm tracking-[0.22em] uppercase"
            >
              Text {CONTACT.phoneDisplay}
            </a>
          </div>
        </div>
      </div>
    </>
  );
}
